export type CoachLinkedContextSource = 'knowledge' | 'blog' | 'exercise' | 'workout';

export interface CoachLinkedContext {
  source: CoachLinkedContextSource;
  title: string;
  returnTo: string;
}

const CONTEXT_PREFIX = 'coach.linked-context.v1:';
const memoryContexts = new Map<string, CoachLinkedContext>();

function userKey(userId?: number): string {
  return String(userId ?? 'anonymous');
}

function isSource(value: unknown): value is CoachLinkedContextSource {
  return value === 'knowledge'
    || value === 'blog'
    || value === 'exercise'
    || value === 'workout';
}

function safeReturnPath(value: unknown): string | undefined {
  if (typeof value !== 'string' || !value.startsWith('/') || value.startsWith('//')) {
    return undefined;
  }
  if (value.includes('\\') || Array.from(value).some((character) => character.charCodeAt(0) < 32)) {
    return undefined;
  }
  return value;
}

function normalizeContext(value: unknown): CoachLinkedContext | undefined {
  if (!value || typeof value !== 'object') return undefined;
  const candidate = value as Partial<CoachLinkedContext>;
  const title = typeof candidate.title === 'string' ? candidate.title.trim() : '';
  const returnTo = safeReturnPath(candidate.returnTo);
  if (!isSource(candidate.source) || !title || !returnTo) return undefined;
  return { source: candidate.source, title: title.slice(0, 180), returnTo };
}

export function coachLinkedContextKey(userId?: number, threadId?: string): string {
  return `${CONTEXT_PREFIX}${userKey(userId)}:${threadId ?? 'current'}`;
}

export function rememberCoachLinkedContext(
  context: CoachLinkedContext,
  userId?: number,
  threadId?: string,
): void {
  const normalized = normalizeContext(context);
  if (!normalized || !threadId) return;
  const key = coachLinkedContextKey(userId, threadId);
  memoryContexts.set(key, normalized);
  try {
    sessionStorage.setItem(key, JSON.stringify(normalized));
  } catch {
    return;
  }
}

export function loadCoachLinkedContext(
  userId?: number,
  threadId?: string,
): CoachLinkedContext | undefined {
  if (!threadId) return undefined;
  const key = coachLinkedContextKey(userId, threadId);
  try {
    const raw = sessionStorage.getItem(key);
    if (raw) {
      const parsed = normalizeContext(JSON.parse(raw));
      if (parsed) {
        memoryContexts.set(key, parsed);
        return parsed;
      }
      sessionStorage.removeItem(key);
    }
  } catch {
    return memoryContexts.get(key);
  }
  return memoryContexts.get(key);
}

export function coachLinkedContextFromSearch(
  params: URLSearchParams,
): CoachLinkedContext | undefined {
  const source = params.get('from');
  const title = params.get('title');
  const returnTo = safeReturnPath(params.get('return'));
  if (!isSource(source) || !title?.trim() || !returnTo) return undefined;
  return normalizeContext({ source, title, returnTo });
}

export function clearCoachLinkedContexts(userId?: number): void {
  const prefix = `${CONTEXT_PREFIX}${userKey(userId)}:`;
  for (const key of memoryContexts.keys()) {
    if (key.startsWith(prefix)) memoryContexts.delete(key);
  }
  try {
    for (let index = sessionStorage.length - 1; index >= 0; index -= 1) {
      const key = sessionStorage.key(index);
      if (key?.startsWith(prefix)) sessionStorage.removeItem(key);
    }
  } catch {
    return;
  }
}
